/**
 * R4 — parsers are total. They return a `Result` rather than throwing, so a single malformed
 * item can drop itself without taking down the list it lives in.
 *
 * Plain object union (R3): survives a JSON round-trip and ports to Kotlin as a sealed class.
 */
export type Result<T, E> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E };

export function ok<T>(value: T): { readonly ok: true; readonly value: T } {
  return { ok: true, value };
}

export function err<E>(error: E): { readonly ok: false; readonly error: E } {
  return { ok: false, error };
}

export type ParseFailureReason = 'missing' | 'wrong-type' | 'empty' | 'malformed';

/**
 * Machine-readable only — no prose, no formatting (R7). The UI decides what a user sees; this
 * exists so a dropped item can be counted and logged rather than vanishing silently.
 */
export interface ParseError {
  readonly entity: string;
  readonly field: string | null;
  readonly reason: ParseFailureReason;
}

export function parseError(
  entity: string,
  field: string | null,
  reason: ParseFailureReason,
): ParseError {
  return { entity, field, reason };
}

/** Result of parsing a list under R4's drop-malformed-items discipline. */
export interface ParsedList<T> {
  readonly items: readonly T[];
  readonly dropped: readonly ParseError[];
}
